import React, { useContext, useMemo } from 'react';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { FiltersContextType, ListOfItems } from './types';
import {
    getListOfItems,
    getListOfTagsCategories,
} from './helpers/getListOfItems';

export const DefaultFiltersContext: FiltersContextType = {
    listOfInstruments: undefined,
    listOfGenres: undefined,
    listOfSfxs: undefined,
    listOfTypes: undefined,
    listOfTags: undefined,
    listOfTagsCategories: undefined,
};

export const FiltersContext = React.createContext<FiltersContextType>(
    DefaultFiltersContext,
);

export const useFiltersContext = (): FiltersContextType =>
    useContext(FiltersContext);

export const FiltersProvider = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    const { apiFilters } = useApiFiltersContext();
    const {
        filterTypesMeta,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterSfxsMeta,
        filterTagsMeta,
    } = useSearchController();

    const listOfInstruments: ListOfItems = useMemo(
        () =>
            getListOfItems({
                filters: apiFilters.instruments,
                searchFilterMeta: filterInstrumentsMeta,
            }),
        [apiFilters, filterInstrumentsMeta],
    );

    const listOfGenres: ListOfItems = useMemo(
        () =>
            getListOfItems({
                filters: apiFilters.genres,
                searchFilterMeta: filterGenresMeta,
            }),
        [apiFilters, filterGenresMeta],
    );

    const listOfSfxs: ListOfItems = useMemo(
        () =>
            getListOfItems({
                filters: apiFilters.sfxs,
                searchFilterMeta: filterSfxsMeta,
            }),
        [apiFilters, filterSfxsMeta],
    );

    const listOfTags: ListOfItems = useMemo(
        () =>
            getListOfItems({
                filters: apiFilters.tags,
                searchFilterMeta: filterTagsMeta,
            }),
        [apiFilters, filterTagsMeta],
    );

    const listOfTagsCategories: ListOfItems = useMemo(
        () =>
            getListOfTagsCategories({
                filters: apiFilters.tagsCategories,
                searchFilterMeta: filterTagsMeta,
            }),
        [apiFilters, filterTagsMeta],
    );

    const listOfTypes: ListOfItems = useMemo(
        () =>
            getListOfItems({
                filters: apiFilters.sampleTypes,
                searchFilterMeta: filterTypesMeta,
                isReversed: true,
            }),
        [apiFilters, filterTypesMeta],
    );

    const context = {
        listOfInstruments: listOfInstruments,
        listOfGenres: listOfGenres,
        listOfSfxs: listOfSfxs,
        listOfTypes: listOfTypes,
        listOfTags: listOfTags,
        listOfTagsCategories: listOfTagsCategories,
    };

    return (
        <FiltersContext.Provider value={context}>
            {children}
        </FiltersContext.Provider>
    );
};

export default FiltersProvider;
